// Nine industry pages served at /industries/:slug.

export const INDUSTRIES = {
  "logistics-transportation-in-oman": {
    slug: "logistics-transportation-in-oman",
    path: "/industries/logistics-transportation-in-oman",
    navLabel: "Logistics & transportation",
    eyebrow: "Industries",
    h1: "Logistics and Transportation Software in Oman",
    meta: { title: "Logistics and Transportation Software in Oman", description: "Dispatch, driver apps, warehouse and logistics ERP — built by an Oman-based company that is building a logistics platform of its own." },
    intro: "We do not just build logistics software. We are building a logistics platform of our own — customer and driver apps, auto-dispatch and a B2B shipper portal, designed, built and operated by us in Oman.",
    sections: [
      { type: "bullets", heading: "What this sector typically struggles with", items: [
        "Drivers assigned by phone call",
        "Customers ringing to ask where their parcel is",
        "Proof of delivery on paper",
        "Pricing that differs per client and lives in someone's head",
        "No idea which routes lose money",
      ] },
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Customer apps", desc: "Book, pay, track, rate." },
        { title: "Driver apps", desc: "Offers, routing, GPS, proof of delivery." },
        { title: "Dispatch engines", desc: "Auto-assignment with cascade and fallback." },
        { title: "Shipper portals", desc: "B2B, contract-priced, self-serve." },
        { title: "Warehouse management", desc: "Receiving to dispatch, bin accuracy." },
        { title: "Logistics ERP", desc: "Contracts, invoicing, reporting." },
      ] },
      { type: "callout", text: "Proof: our delivery platform (Oman, ready for launch), our warehouse system (Singapore, Malaysia and USA, delivered), our logistics ERP (USA, delivered), and our field-service dispatch system (USA, live)." },
      { type: "bullets", heading: "Rules and standards that apply", items: ["Oman PDPL", "Traffic and transport regulations", "VAT and invoicing"] },
      { type: "faq", items: [
        { q: "Can you take over an existing dispatch system?", a: "Yes — after a ten-day audit that gives you an honest fix/rebuild recommendation." },
        { q: "How does auto-dispatch handle patchy signal?", a: "Offers cascade with timeouts, fall back to manual assignment, and driver responses queue offline until signal returns." },
        { q: "White-label for our shipper customers?", a: "Yes — every shipper on our platform can have its own branding and rate card." },
      ] },
    ],
    related: [
      { tag: "Solutions", label: "On-demand delivery platform", to: "/solutions/on-demand-delivery-platform" },
      { tag: "Solutions", label: "Logistics & fleet software", to: "/solutions/logistics-fleet-software" },
      { tag: "Work", label: "Delivery & logistics platform", to: "/work/delivery-logistics-platform" },
    ],
  },

  "retail-e-commerce-in-oman": {
    slug: "retail-e-commerce-in-oman",
    path: "/industries/retail-e-commerce-in-oman",
    navLabel: "Retail & e-commerce",
    eyebrow: "Industries",
    h1: "Retail and E-commerce Software in Oman",
    meta: { title: "Retail and E-commerce Software in Oman", description: "Storefronts are the easy part. We build the operations behind them — inventory, orders, returns, POS integration." },
    intro: "The store is the easy part. Inventory accuracy, delivery, returns, payment reconciliation and customer service are where online retail actually fails. We build the operations that make an online retailer boring to run — and boring is what you want. Our own marketplace platform sits behind this capability.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Custom e-commerce", desc: "When Shopify no longer fits." },
        { title: "Multi-vendor marketplaces", desc: "Vendors, commissions, split orders." },
        { title: "Shopify & headless", desc: "When it does fit — done properly." },
        { title: "Inventory & OMS", desc: "One number of stock across channels." },
        { title: "Loyalty", desc: "Points, tiers, redemptions." },
        { title: "Delivery & POS integration", desc: "Online and in-store on one record." },
      ] },
      { type: "callout", text: "In build: our own multi-vendor marketplace platform for the Oman and GCC market." },
      { type: "faq", items: [
        { q: "Custom or Shopify?", a: "We recommend based on where you are. Shopify is superb for storefronts, custom wins when your operations diverge from the norm." },
        { q: "Migration off Magento or WooCommerce?", a: "Yes — customers, orders, catalogue and history preserved." },
        { q: "Local payments?", a: "Yes — all major Omani processors, wallets and BNPL where relevant." },
      ] },
    ],
    related: [
      { tag: "Solutions", label: "E-commerce marketplace", to: "/solutions/e-commerce-marketplace" },
      { tag: "Solutions", label: "Warehouse management", to: "/solutions/warehouse-management" },
      { tag: "Services", label: "Web design & development", to: "/services/web-design-company-in-oman" },
    ],
  },

  "government-public-sector-oman": {
    slug: "government-public-sector-oman",
    path: "/industries/government-public-sector-oman",
    navLabel: "Government & public sector",
    eyebrow: "Industries",
    h1: "Government and Public Sector Software in Oman",
    meta: { title: "Government and Public Sector Software in Oman", description: "Bilingual, auditable citizen portals and workflow systems. Meyar for governance and evidence." },
    intro: "We are based in Oman, we build bilingual by default, and Meyar was built for exactly the governance and evidence requirements public sector bodies work under. This page describes what we build for the sector — it is not a claim of existing engagements.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Citizen portals", desc: "Bilingual, accessible, auditable." },
        { title: "Workflow & approval systems", desc: "Delegation, escalation, records." },
        { title: "Inspection & field apps", desc: "Offline-capable, photo evidence." },
        { title: "Dashboards", desc: "For directors and the minister's office." },
        { title: "Document management", desc: "Version control, retention, disposal." },
        { title: "GRC platforms", desc: "Meyar for governance, risk and compliance." },
        { title: "Integration with existing systems", desc: "Legacy ERP, identity, national services." },
      ] },
      { type: "bullets", heading: "Standards we build to", items: [
        "Oman PDPL (RD 6/2022, MD 34/2024)",
        "Requirements of the Cyber Defence Centre (RD 64/2020)",
        "ISO 27001",
        "WCAG accessibility",
        "Full Arabic",
      ] },
      { type: "faq", items: [
        { q: "Do you deliver for public bodies today?", a: "This page describes what we build for the sector; it does not claim existing engagements. Where relevant we'd be happy to walk through what a Wafa build looks like for a public-sector context." },
        { q: "Can you handle security clearance requirements?", a: "For nominated engineers, yes." },
        { q: "In-country processing?", a: "Tell us at the start and we design to it. Retrofitting residency is expensive." },
      ] },
    ],
    related: [
      { tag: "Meyar", label: "Meyar Enterprise GRC", to: "/meyar/enterprise-grc" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
      { tag: "Services", label: "Cloud & data residency", to: "/services/cloud-devops-oman" },
    ],
  },

  "banking-financial-services-oman": {
    slug: "banking-financial-services-oman",
    path: "/industries/banking-financial-services-oman",
    navLabel: "Banking & financial services",
    eyebrow: "Industries",
    h1: "Banking and Financial Services Software in Oman",
    meta: { title: "Banking and Financial Services Software in Oman", description: "Portals, digital onboarding, KYC, loan processing and risk built around your regulated core." },
    intro: "Onboarding that takes days, compliance evidence scattered across departments, and customer-facing systems that look a decade old — those are the problems we build for in this sector. We build around your regulated core; we do not replace it. This page describes capability and fit, not existing engagements.",
    sections: [
      { type: "bullets", heading: "What this sector typically struggles with", items: [
        "Onboarding that takes days",
        "Compliance evidence scattered across departments",
        "Customer-facing systems that look a decade old",
      ] },
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Customer portals", desc: "Modern, mobile-first, bilingual." },
        { title: "Digital onboarding & KYC", desc: "Documents, liveness, screening." },
        { title: "Loan processing", desc: "Application to disbursal, with audit at every step." },
        { title: "Risk & compliance", desc: "Meyar for enterprise risk and controls." },
        { title: "Reporting & reconciliation", desc: "Regulator, board and daily ops on the same numbers." },
        { title: "Core banking integration", desc: "Around your regulated core — not replacing it." },
      ] },
      { type: "callout", text: "We build around your regulated core, we do not replace it." },
      { type: "faq", items: [
        { q: "Do you build core banking systems?", a: "No. We build the customer-facing and risk-facing layers around them." },
        { q: "Do you work with CBO reporting formats?", a: "Yes — Meyar and our custom reporting modules generate the returns and schedule filing." },
        { q: "SOC-style monitoring?", a: "Meyar governs your security controls but is not a monitoring platform. Monitoring itself is done by your SOC or MSSP." },
        { q: "Do you deliver for banks today?", a: "This page describes what we build for the sector; it does not claim existing engagements." },
      ] },
    ],
    related: [
      { tag: "Meyar", label: "Meyar Enterprise GRC", to: "/meyar/enterprise-grc" },
      { tag: "Meyar", label: "Meyar Cyber Security GRC", to: "/meyar/cyber-security-grc" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
    ],
  },

  "healthcare-in-oman": {
    slug: "healthcare-in-oman",
    path: "/industries/healthcare-in-oman",
    navLabel: "Healthcare",
    eyebrow: "Industries",
    h1: "Healthcare Software in Oman",
    meta: { title: "Healthcare Software in Oman", description: "Clinic and hospital management, patient portals, telemedicine — designed around the PDPL sensitive-data rules." },
    intro: "Health information is sensitive personal data under the PDPL, and processing it outside Oman requires Ministry approval. We map that with you before a line of code is written, so the decision is deliberate and documented. This page describes what we build for the sector, not existing engagements.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Clinic & hospital management", desc: "Patients, appointments, EHR, billing." },
        { title: "Appointments & reminders", desc: "Bilingual, SMS and WhatsApp." },
        { title: "Patient portals", desc: "Self-service records and results." },
        { title: "Healthcare CRM", desc: "Enquiry to consultation to follow-up." },
        { title: "Telemedicine", desc: "Consultations, records, prescriptions." },
        { title: "Pharmacy & inventory", desc: "Stock, batches, expiry, dispensing." },
        { title: "Insurance claims", desc: "Codes, adjudication, tracking." },
      ] },
      { type: "faq", items: [
        { q: "PDPL and sensitive health data?", a: "Health information is a sensitive category under the PDPL. Processing it outside Oman requires Ministry approval — we design the data flow so that decision is deliberate." },
        { q: "Do you integrate with existing HIS systems?", a: "Yes — HL7, FHIR and vendor-specific APIs. We build the module you need without replacing the core system." },
        { q: "Insurance panel integration?", a: "Yes, panel-specific claim files and eligibility checks." },
        { q: "Do you deliver for healthcare providers today?", a: "This page describes what we build for the sector; it does not claim existing engagements." },
      ] },
    ],
    related: [
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Services", label: "Mobile app development", to: "/services/mobile-app-development-company-in-oman" },
      { tag: "Services", label: "Cloud & data residency", to: "/services/cloud-devops-oman" },
    ],
  },

  "real-estate-facilities-oman": {
    slug: "real-estate-facilities-oman",
    path: "/industries/real-estate-facilities-oman",
    navLabel: "Real estate & facilities",
    eyebrow: "Industries",
    h1: "Real Estate and Facilities Software in Oman",
    meta: { title: "Real Estate and Facilities Software in Oman", description: "Property CRM, community and building apps, and facilities management." },
    intro: "Property is a data business dressed up as a brick-and-mortar business. This page describes what we build for the sector, framed around what platforms of ours are designed for — not a list of existing engagements.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Listing portals", desc: "Search, viewings, enquiries." },
        { title: "Real estate CRM", desc: "Lead scoring, viewing management, negotiations." },
        { title: "Community & building apps", desc: "Access, visitors, maintenance, payments." },
        { title: "Facilities & work orders", desc: "Requests, dispatch, PPM." },
        { title: "Tenant & owner portals", desc: "Statements, requests, communications." },
        { title: "Developer sales management", desc: "Units, blocks, contracts, handover." },
      ] },
      { type: "faq", items: [
        { q: "Can it integrate with our accounting?", a: "Yes — Tally, Zoho Books, QuickBooks and custom finance systems." },
        { q: "Access control hardware?", a: "We integrate with common controllers via API." },
        { q: "Payment collection?", a: "Yes — recurring and one-off, local processors." },
      ] },
    ],
    related: [
      { tag: "Solutions", label: "Community & building app", to: "/solutions/community-building-app" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
      { tag: "Services", label: "Mobile app development", to: "/services/mobile-app-development-company-in-oman" },
    ],
  },

  "education-in-oman": {
    slug: "education-in-oman",
    path: "/industries/education-in-oman",
    navLabel: "Education",
    eyebrow: "Industries",
    h1: "Education Software in Oman",
    meta: { title: "Education Software in Oman", description: "School management, LMS, admissions CRM, parent apps and fee management." },
    intro: "Education institutions carry more sensitive data than most banks — minors' records, health information, financial arrangements. This page describes what we build for the sector; it does not claim existing engagements.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "School & institute management", desc: "Students, staff, classes, records." },
        { title: "LMS", desc: "Courses, assessments, content library." },
        { title: "Admissions CRM", desc: "Enquiries to enrolment, in one thread." },
        { title: "Parent & student apps", desc: "Communications, attendance, grades." },
        { title: "Fee management", desc: "Invoicing, collection, reminders." },
        { title: "Attendance & timetabling", desc: "By class, by student, by teacher." },
        { title: "Exams & results", desc: "From marking to transcripts." },
      ] },
      { type: "faq", items: [
        { q: "Bilingual?", a: "Yes — Arabic and English, side by side or switched per user." },
        { q: "Integrations?", a: "SIS, LMS, accounting, SMS gateways and government portals." },
        { q: "Privacy for minors?", a: "PDPL includes children's data. We treat it accordingly and design consent flows for parents." },
      ] },
    ],
    related: [
      { tag: "Services", label: "Mobile app development", to: "/services/mobile-app-development-company-in-oman" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
    ],
  },

  "manufacturing-distribution-oman": {
    slug: "manufacturing-distribution-oman",
    path: "/industries/manufacturing-distribution-oman",
    navLabel: "Manufacturing & distribution",
    eyebrow: "Industries",
    h1: "Manufacturing and Distribution Software in Oman",
    meta: { title: "Manufacturing and Distribution Software in Oman", description: "Production planning, quality, maintenance and dealer portals — joined up around the SKU." },
    intro: "Production, quality, maintenance and sales all belong on the same record — because in manufacturing, that record is the product moving through your business. This page describes what we build for the sector; it does not claim existing engagements.",
    sections: [
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Production planning", desc: "Orders, BOMs, capacity, sequencing." },
        { title: "Inventory & raw materials", desc: "By location, by batch, by expiry." },
        { title: "Quality management", desc: "Inspections, non-conformance, corrective actions." },
        { title: "Maintenance scheduling", desc: "Preventive and breakdown, with cost tracking." },
        { title: "Distributor & dealer portals", desc: "Orders, credit, returns." },
        { title: "Route management", desc: "Van sales and pre-order." },
        { title: "Warehouse management", desc: "Receipts to dispatch, bin-level accuracy." },
      ] },
      { type: "faq", items: [
        { q: "Integrate with our existing ERP?", a: "Yes — SAP, Oracle, Odoo and custom. We build the module specific to your operations without replacing the core." },
        { q: "Barcode and RFID?", a: "Both — the choice depends on your workflow and the density of your inventory." },
      ] },
    ],
    related: [
      { tag: "Solutions", label: "Warehouse management", to: "/solutions/warehouse-management" },
      { tag: "Services", label: "ERP & HRMS", to: "/services/erp-software-development-company-in-oman" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
    ],
  },

  "automotive-in-oman": {
    slug: "automotive-in-oman",
    path: "/industries/automotive-in-oman",
    navLabel: "Automotive",
    eyebrow: "Industries",
    h1: "Automotive Software in Oman",
    meta: { title: "Automotive Software in Oman", description: "Showroom management, public listings, service booking and workshop, parts inventory, finance integration." },
    intro: "The vehicle is the record. Sales, service, parts and finance should all join up around the VIN — not around the department that owns the ledger. Our showroom platform is live in Muscat.",
    sections: [
      { type: "callout", text: "Proof: our showroom platform is live in Muscat — vehicle inventory including customer consignment, sales, leads, reservations, payments, documents, printable sale authorisation, inspection release and test drive forms, reporting, plus a public listing website." },
      { type: "grid", heading: "What we build for the sector", items: [
        { title: "Showroom & dealership", desc: "Inventory (owned and consignment), sales, leads." },
        { title: "Public listing sites", desc: "Consumer-facing search and enquiries." },
        { title: "Automotive CRM", desc: "Enquiry to sale to service to next sale." },
        { title: "Service booking & workshop", desc: "Bays, technicians, jobs, invoicing." },
        { title: "Parts inventory", desc: "By VIN and by SKU." },
        { title: "Finance integration", desc: "Bank and finance company APIs where available." },
      ] },
      { type: "faq", items: [
        { q: "Consignment vehicles?", a: "Yes — separate ownership, commission structure and payout tracking." },
        { q: "Printable forms?", a: "Yes — sale authorisation, inspection release, test drive, all A4 and Arabic-ready." },
      ] },
    ],
    related: [
      { tag: "Solutions", label: "Showroom management", to: "/solutions/showroom-management" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
      { tag: "Work", label: "Showroom management platform", to: "/work/showroom-management-platform" },
    ],
  },
};

export const INDUSTRY_LIST = Object.values(INDUSTRIES);
